import type { FormStep } from '../content/applicationForm';
import type { SiteContent } from '../content/types';
import type { Litter } from './api';

export type ApplicationStatus = 'new' | 'reviewing' | 'approved' | 'declined';
export type Application = { id: string; applicantName: string; applicantEmail: string; answers: Record<string, unknown>; status: ApplicationStatus; submittedAt: string };
export type EmailDirection = 'inbound' | 'outbound' | 'draft';
export type EmailAttachment = { id: string; filename: string | null; size: number; contentType: string; contentDisposition: string | null; contentId: string | null };
export type Email = { id: string; threadId: string; direction: EmailDirection; fromEmail: string; fromName: string | null; replyTo: string | null; toEmail: string; subject: string; text: string; html: string | null; resendId: string | null; isRead: number; isStarred: number; archivedAt: string | null; deletedAt: string | null; createdAt: string; updatedAt: string; attachments: EmailAttachment[] };
export type EmailStateChanges = { isRead?: boolean; isStarred?: boolean; archived?: boolean; deleted?: boolean };
export type EmailDraftInput = { threadId?: string; toEmail?: string; subject?: string; text?: string; html?: string | null };
export type ContactMessage = { id: string; firstName: string; lastName: string; email: string; message: string; createdAt: string };
export type Subscriber = { id: string; email: string; createdAt: string };
export type MediaItem = { key: string; url: string; size: number; uploadedAt: string };
export type EmailStatus = {
  configured: boolean;
  outboundConfigured: boolean;
  inboundConfigured: boolean;
  from: string;
  breederEmail: string;
  inboundAddress: string | null;
  forwardingTo: string;
};
export type DeliveryResult = { ok: true; configured: boolean; delivered: boolean };
export type ConnectionsStatus = {
  migrated: string[];
  email: { configured: boolean; inboundConfigured: boolean; from: string };
  media: { configured: boolean; bucket: string | null; publicHost: string | null };
  site: { host: string | null };
};
export type Overview = {
  counts: {
    newApplications: number;
    totalApplications: number;
    unreadEmails: number;
    websiteInquiries: number;
    draftEmails: number;
    liveLitters: number;
    subscribers: number;
  };
  recentApplications: Application[];
  recentEmails: Email[];
};
export type AdminState = { content: SiteContent; formConfig: { steps: FormStep[] } | null; litters: Litter[]; applications: Application[]; contactMessages: ContactMessage[]; subscribers: Subscriber[] };

class ApiError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: init.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new ApiError(detail.error ?? 'Request failed.', response.status);
  }
  return response.status === 204 ? (undefined as T) : (response.json() as Promise<T>);
}

export const adminApi = {
  isAuthError: (error: unknown): boolean => error instanceof ApiError && error.status === 401,

  getSession: () => request<{ authenticated: boolean; passwordSet: boolean }>('/api/admin/session'),
  login: (password: string) => request<{ ok: true }>('/api/admin/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),

  getOverview: () => request<Overview>('/api/admin/overview'),
  getState: () => request<AdminState>('/api/admin/state'),

  getEmailStatus: () => request<EmailStatus>('/api/admin/email/status'),
  sendTestEmail: (to?: string) => request<DeliveryResult & { to: string }>('/api/admin/email/test', { method: 'POST', body: JSON.stringify(to ? { to } : {}) }),

  getConnections: () => request<ConnectionsStatus>('/api/admin/connections'),
  saveConnections: (migrated: string[]) => request<{ ok: true; migrated: string[] }>('/api/admin/connections', { method: 'PUT', body: JSON.stringify({ migrated }) }),

  getEmails: () => request<{ emails: Email[] }>('/api/admin/emails').then((data) => data.emails),
  getEmailAttachmentUrl: (emailId: string, attachmentId: string) => `/api/admin/emails/${encodeURIComponent(emailId)}/attachments/${encodeURIComponent(attachmentId)}`,
  markEmailRead: (id: string, read = true) => request<{ email: Email }>(`/api/admin/emails/${id}/read`, { method: 'POST', body: JSON.stringify({ read }) }).then((data) => data.email),
  updateEmailState: (id: string, changes: EmailStateChanges) => request<{ email: Email }>(`/api/admin/emails/${encodeURIComponent(id)}/state`, { method: 'PATCH', body: JSON.stringify(changes) }).then((data) => data.email),
  updateEmailBulkState: (targets: { ids?: string[]; threadIds?: string[] }, changes: EmailStateChanges) => request<{ emails: Email[]; updated: number }>('/api/admin/emails/bulk/state', { method: 'PATCH', body: JSON.stringify({ ...targets, changes }) }),
  permanentlyDeleteEmails: (targets: { ids?: string[]; threadIds?: string[] }) => request<{ deleted: number; deletedIds: string[] }>('/api/admin/emails/bulk/delete', { method: 'POST', body: JSON.stringify(targets) }),
  permanentlyDeleteEmail: (id: string) => request<void>(`/api/admin/emails/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  replyEmail: (id: string, body: string, subject?: string) => request<DeliveryResult & { email: Email }>(`/api/admin/emails/${id}/reply`, { method: 'POST', body: JSON.stringify({ body, subject }) }),

  getEmailDrafts: () => request<{ drafts: Email[] }>('/api/admin/email-drafts').then((data) => data.drafts),
  createEmailDraft: (draft: EmailDraftInput) => request<{ draft: Email }>('/api/admin/email-drafts', { method: 'POST', body: JSON.stringify(draft) }).then((data) => data.draft),
  updateEmailDraft: (id: string, draft: EmailDraftInput) => request<{ draft: Email }>(`/api/admin/email-drafts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(draft) }).then((data) => data.draft),
  deleteEmailDraft: (id: string) => request<void>(`/api/admin/email-drafts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  sendEmailDraft: (id: string) => request<DeliveryResult & { email: Email }>(`/api/admin/email-drafts/${encodeURIComponent(id)}/send`, { method: 'POST' }),

  getApplications: () => request<{ applications: Application[] }>('/api/admin/applications').then((data) => data.applications),
  setApplicationStatus: (id: string, status: ApplicationStatus) => request<{ application: Application }>(`/api/admin/applications/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }).then((data) => data.application),
  deleteApplication: (id: string) => request<void>(`/api/admin/applications/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  replyApplication: (id: string, body: string, subject?: string) => request<DeliveryResult & { email: Email }>(`/api/admin/applications/${id}/reply`, { method: 'POST', body: JSON.stringify({ body, subject }) }),
  replyContactMessage: (id: string, body: string, subject?: string) => request<DeliveryResult & { email: Email }>(`/api/admin/contact-messages/${id}/reply`, { method: 'POST', body: JSON.stringify({ body, subject }) }),

  saveContent: (content: SiteContent) => request<{ content: SiteContent }>('/api/admin/content', { method: 'PUT', body: JSON.stringify(content) }),
  saveFormConfig: (steps: FormStep[]) => request('/api/admin/form-config', { method: 'PUT', body: JSON.stringify({ steps }) }),
  saveLitters: (litters: Litter[]) => request<{ litters: Litter[] }>('/api/admin/litters', { method: 'PUT', body: JSON.stringify({ litters }) }),

  broadcast: (subject: string, body: string) => request<DeliveryResult & { sent: number }>('/api/admin/newsletter/broadcast', { method: 'POST', body: JSON.stringify({ subject, body }) }),

  setPassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/admin/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),

  listMedia: () => request<{ items: MediaItem[] }>('/api/admin/media').then((data) => data.items),

  uploadMedia: (file: File, metadata: Record<string, string> = {}) => {
    const query = new URLSearchParams(metadata).toString();
    return request<{ id: string; deliveryURL: string }>(`/api/admin/media${query ? `?${query}` : ''}`, {
      method: 'POST',
      body: file,
      headers: { 'content-type': file.type || 'application/octet-stream' },
    });
  },
};
